import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import {
  mediaQueryLarge,
  getIOSVersion,
  fetchConfig,
  isMobileBreakpoint,
  prefersReducedMotion,
  estimateCartItemCountAfterAdd,
} from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
/**
 * @param {HTMLTemplateElement} template
 * @returns {Node[] | null}
 */
function getTemplateContentNodes(template) {
  if (!(template instanceof HTMLTemplateElement)) return null;

  const html = template.innerHTML.trim();
  if (!html) return null;

  const container = document.createElement('div');
  container.innerHTML = html;
  return container.childNodes.length > 0 ? Array.from(container.childNodes) : null;
}

/**
 * @param {Element | null | undefined} quickAddComponent
 * @returns {Node[] | null}
 */
function getSizeGuideContentNodes(quickAddComponent) {
  const mulayaGuide = quickAddComponent?.querySelector('.quick-add__size-guide-content mulaya-size-guide');
  if (mulayaGuide instanceof HTMLElement) {
    return [mulayaGuide.cloneNode(true)];
  }

  const hiddenProductContent = quickAddComponent?.querySelector('.quick-add__size-guide-content');
  if (hiddenProductContent?.innerHTML.trim()) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = hiddenProductContent.innerHTML;
    return Array.from(wrapper.childNodes);
  }

  const productTemplate = quickAddComponent?.querySelector('template.quick-add__size-guide-template');
  const productNodes = getTemplateContentNodes(productTemplate);
  if (productNodes) return productNodes;

  const defaultTemplate = document.getElementById('quick-add-size-guide-default-template');
  const defaultNodes = getTemplateContentNodes(defaultTemplate);
  if (defaultNodes) return defaultNodes;

  const hiddenContent = document.getElementById('quick-add-size-guide-default-content');
  if (hiddenContent?.innerHTML.trim()) {
    const wrapper = document.createElement('div');
    wrapper.className = 'rte text-block';
    wrapper.innerHTML = hiddenContent.innerHTML;
    return [wrapper];
  }

  return null;
}

/**
 * @param {Element | null | undefined} quickAddComponent
 * @returns {boolean}
 */
function hasSizeGuideContent(quickAddComponent) {
  return getSizeGuideContentNodes(quickAddComponent) !== null;
}

/**
 * @returns {boolean}
 */
function isSizeGuideConfigured() {
  const dialog = document.getElementById('quick-add-dialog');
  return dialog?.dataset.sizeGuideConfigured === 'true';
}

/**
 * @returns {boolean}
 */
function shouldShowSizeGuideButton(quickAddComponent) {
  return hasSizeGuideContent(quickAddComponent) || isSizeGuideConfigured();
}

/**
 * @param {HTMLElement} dialog
 * @returns {HTMLElement}
 */
function createSizeGuideEmptyMessage(dialog) {
  const message = document.createElement('p');
  message.className = 'quick-add-modal__size-guide-empty';
  message.textContent = dialog.dataset.sizeGuideEmptyLabel || 'Size guide content is not available.';
  return message;
}

/** Shared across all quick-add instances so hover/touch prefetch is reused on open. */
/** @type {Map<string, Element>} */
const quickAddGridCache = new Map();
/** @type {Map<string, Promise<Element | null>>} */
const quickAddGridInflight = new Map();
const QUICK_ADD_CACHE_LIMIT = 24;
/** Max parallel background prefetches — protects PLP scroll/network. */
const QUICK_ADD_MAX_BACKGROUND = 2;
/** @type {number} */
let quickAddBackgroundFetches = 0;
/** @type {Array<() => void>} */
const quickAddBackgroundQueue = [];

/**
 * @param {() => Promise<Element | null>} start
 * @returns {Promise<Element | null>}
 */
function runQuickAddBackgroundFetch(start) {
  return new Promise((resolve, reject) => {
    const run = () => {
      quickAddBackgroundFetches += 1;
      start()
        .then(resolve, reject)
        .finally(() => {
          quickAddBackgroundFetches -= 1;
          const next = quickAddBackgroundQueue.shift();
          if (next) next();
        });
    };

    if (quickAddBackgroundFetches < QUICK_ADD_MAX_BACKGROUND) {
      run();
    } else {
      quickAddBackgroundQueue.push(run);
    }
  });
}

/**
 * Cache by product path so variant query changes still hit.
 * @param {string} productPageUrl
 * @returns {string}
 */
function getQuickAddCacheKey(productPageUrl) {
  try {
    return new URL(productPageUrl, window.location.origin).pathname;
  } catch {
    return productPageUrl;
  }
}

/**
 * @param {string} key
 * @param {Element} grid
 */
function setQuickAddCachedGrid(key, grid) {
  if (quickAddGridCache.has(key)) {
    quickAddGridCache.delete(key);
  }

  quickAddGridCache.set(key, grid);

  while (quickAddGridCache.size > QUICK_ADD_CACHE_LIMIT) {
    const oldestKey = quickAddGridCache.keys().next().value;
    if (oldestKey === undefined) break;
    quickAddGridCache.delete(oldestKey);
  }
}

/**
 * @param {string} htmlText
 * @returns {Element | null}
 */
function extractProductGridFromHtml(htmlText) {
  const html = new DOMParser().parseFromString(htmlText, 'text/html');
  const gridElement = html.querySelector('[data-product-grid-content]');
  if (!gridElement) return null;

  const grid = /** @type {Element} */ (gridElement.cloneNode(true));
  grid.querySelectorAll('script, noscript').forEach((el) => el.remove());
  return grid;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #lifecycleAbortController = null;
  /** @type {AbortController | null} */
  #pillDismissAbortController = null;
  /** @type {number | undefined} */
  #pillCollapseFallbackTimeout = undefined;
  /** @type {boolean} */
  #drawerAddInFlight = false;
  /** @type {boolean} */
  #modalOpenInFlight = false;

  /** @type {Array<{id: number, available: boolean, option1: string | null, option2: string | null, option3: string | null}> | null} */
  #variantData = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#lifecycleAbortController?.abort();
    this.#lifecycleAbortController = new AbortController();
    const { signal } = this.#lifecycleAbortController;

    mediaQueryLarge.addEventListener('change', this.#handleViewportChange, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState, { signal });
    this.addEventListener('pointerdown', this.#prefetchOnGesture, { passive: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#lifecycleAbortController?.abort();
    this.#lifecycleAbortController = null;
    this.#pillDismissAbortController?.abort();
    this.#clearPillCollapseFallback();
    this.#modalOpenInFlight = false;
  }

  #handleViewportChange = () => {
    this.#resetPillState();
    // Only the open instance should close the shared modal (avoids N parallel closeDialog storms on PLP).
    if (!this.hasAttribute('stay-visible')) return;
    this.#closeQuickAddModal();
  };

  /**
   * Starts a background prefetch only when pressing the quick-add control (not while scrolling the card).
   * @param {PointerEvent} event
   */
  #prefetchOnGesture = (event) => {
    if (!(event.target instanceof Element)) return;
    if (
      !event.target.closest(
        '.quick-add__mobile-trigger, .quick-add__button--choose, .quick-add__button--add, .quick-add__pill-add'
      )
    ) {
      return;
    }

    const url = this.productPageUrl;
    if (!url) return;
    this.prefetchProductPage(url);
  };

  /**
   * Soft-invalidate: keep cache for snappy reopen; stock is refreshed on next full navigation.
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;

    this.#resetPillState();
    delete this.dataset.cartDrawerSizesOpen;

    if (this.#isMegaMenuContext() && !isMobileBreakpoint()) {
      this.#closeQuickAddModal();
      this.removeAttribute('stay-visible');
    }
  };

  /**
   * Closes an expanded pill and clears any stale dismiss listeners.
   */
  #resetPillState() {
    this.#pillDismissAbortController?.abort();
    this.#pillDismissAbortController = null;

    const expand = this.querySelector('.quick-add__pill-expand');
    const isExpanded = this.dataset.pillExpanded === 'true';
    const expandIsOpen = expand instanceof HTMLElement && !expand.hidden;

    if (isExpanded || expandIsOpen) {
      this.collapsePill();
      return;
    }

    delete this.dataset.pillExpanded;
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!(variantPicker instanceof VariantPicker)) return;

    try {
      variantPicker.updateVariantPicker(newHtml);
    } catch {
      // Cached HTML may not include a matching variant-picker (e.g. size-only modal).
      return;
    }

    // Morph replaces the fieldset/legend and would remove the size-guide button.
    this.#injectSizeGuide(modalContent);
  }

  /**
   * @returns {Array<{id: number, available: boolean, option1: string | null, option2: string | null, option3: string | null}>}
   */
  #getVariantData() {
    if (this.#variantData) return this.#variantData;

    const jsonEl = this.querySelector('[data-quick-add-variants]');
    if (!(jsonEl instanceof HTMLScriptElement) || !jsonEl.textContent?.trim()) {
      this.#variantData = [];
      return this.#variantData;
    }

    try {
      this.#variantData = JSON.parse(jsonEl.textContent);
    } catch {
      this.#variantData = [];
    }

    return this.#variantData;
  }

  /**
   * @param {number} position
   * @param {{ option1: string | null, option2: string | null, option3: string | null }} variant
   * @returns {string | null}
   */
  #getOptionValueAtPosition(position, variant) {
    if (position === 1) return variant.option1;
    if (position === 2) return variant.option2;
    if (position === 3) return variant.option3;
    return null;
  }

  /**
   * Updates pill size buttons to match the currently selected color/options.
   */
  #updatePillSizeButtons() {
    if (this.dataset.pillHasSizes !== 'true') return;

    const sizePosition = Number(this.dataset.sizeOptionPosition ?? NaN);
    const colorPosition = Number(this.dataset.colorOptionPosition ?? NaN);
    if (!sizePosition) return;

    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[ref="variantId"]') ?? this.querySelector('input[name="id"]')
    );
    if (!variantInput?.value) return;

    const variants = this.#getVariantData();
    const selectedVariant = variants.find((entry) => String(entry.id) === variantInput.value);
    if (!selectedVariant) return;

    const selectedColor =
      colorPosition > 0 ? this.#getOptionValueAtPosition(colorPosition, selectedVariant) : null;

    let anySizeAvailable = false;
    const sizeButtons = this.querySelectorAll('.quick-add__pill-sizes .quick-add__size-option');
    for (const sizeButton of sizeButtons) {
      if (!(sizeButton instanceof HTMLButtonElement)) continue;

      const sizeValue = sizeButton.dataset.sizeValue;
      if (!sizeValue) continue;

      const matchingVariant = variants.find((entry) => {
        const entrySize = this.#getOptionValueAtPosition(sizePosition, entry);
        if (entrySize !== sizeValue || !entry.available) return false;

        if (selectedColor && colorPosition > 0) {
          return this.#getOptionValueAtPosition(colorPosition, entry) === selectedColor;
        }

        return true;
      });

      if (matchingVariant) {
        anySizeAvailable = true;
        sizeButton.disabled = false;
        sizeButton.type = 'submit';
        sizeButton.dataset.variantId = String(matchingVariant.id);
        sizeButton.classList.remove('quick-add__size-option--unavailable');
        sizeButton.classList.add('quick-add__size-option--available');
      } else {
        sizeButton.disabled = true;
        sizeButton.type = 'button';
        delete sizeButton.dataset.variantId;
        sizeButton.classList.remove('quick-add__size-option--available');
        sizeButton.classList.add('quick-add__size-option--unavailable');
      }
    }

    this.#setPillNoSizesState(!anySizeAvailable);
  }

  /**
   * Shows "no size" message when the selected color has no available sizes.
   * @param {boolean} noSizes
   */
  #setPillNoSizesState(noSizes) {
    const sizesBar = this.querySelector('[data-pill-sizes-bar]');
    const noSizesMessage = this.querySelector('[data-pill-no-sizes]');
    const sizeList = this.querySelector('[data-pill-size-list]');

    if (sizesBar instanceof HTMLElement) {
      sizesBar.classList.toggle('quick-add__pill-bar--no-sizes', noSizes);
    }

    if (noSizesMessage instanceof HTMLElement) {
      noSizesMessage.hidden = !noSizes;
    }

    if (sizeList instanceof HTMLElement) {
      sizeList.hidden = noSizes;
    }
  }

  /**
   * Expands or collapses the desktop quick-add pill.
   * @param {Event} [event]
   */
  togglePillExpand = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (isMobileBreakpoint()) return;

    if (this.dataset.pillExpanded === 'true') {
      this.collapsePill();
      return;
    }

    this.#expandPill();
  };

  #expandPill() {
    const expand = this.querySelector('.quick-add__pill-expand');
    const addButton = this.querySelector('.quick-add__pill-choose');

    if (!(expand instanceof HTMLElement)) return;

    this.#updatePillSizeButtons();
    expand.hidden = false;
    expand.style.maxHeight = '0px';
    this.dataset.pillExpanded = 'true';
    addButton?.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        expand.style.maxHeight = `${expand.scrollHeight}px`;
      });
    });

    this.#bindPillDismiss();
  }

  collapsePill() {
    const expand = this.querySelector('.quick-add__pill-expand');
    const addButton = this.querySelector('.quick-add__pill-choose');

    this.#pillDismissAbortController?.abort();
    this.#pillDismissAbortController = null;
    this.#clearPillCollapseFallback();

    if (!(expand instanceof HTMLElement)) {
      delete this.dataset.pillExpanded;
      return;
    }

    delete this.dataset.pillExpanded;
    addButton?.setAttribute('aria-expanded', 'false');

    if (expand.hidden) {
      expand.style.maxHeight = '';
      return;
    }

    let collapseFinished = false;

    const onTransitionEnd = (event) => {
      if (event.target !== expand) return;
      finishCollapse();
    };

    const finishCollapse = () => {
      if (collapseFinished || this.dataset.pillExpanded === 'true') return;

      collapseFinished = true;
      expand.removeEventListener('transitionend', onTransitionEnd);
      expand.hidden = true;
      expand.style.maxHeight = '';
      this.#clearPillCollapseFallback();
    };

    expand.style.maxHeight = `${expand.scrollHeight}px`;
    void expand.offsetHeight;
    expand.style.maxHeight = '0px';

    if (prefersReducedMotion()) {
      finishCollapse();
      return;
    }

    expand.addEventListener('transitionend', onTransitionEnd);
    this.#pillCollapseFallbackTimeout = window.setTimeout(finishCollapse, 600);
  }

  #clearPillCollapseFallback() {
    if (this.#pillCollapseFallbackTimeout === undefined) return;

    clearTimeout(this.#pillCollapseFallbackTimeout);
    this.#pillCollapseFallbackTimeout = undefined;
  }

  #bindPillDismiss() {
    this.#pillDismissAbortController?.abort();
    this.#pillDismissAbortController = new AbortController();
    const { signal } = this.#pillDismissAbortController;

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Node)) return;
        if (this.contains(event.target)) return;
        if (event.target.closest('dialog[open], cart-drawer-component, quick-add-dialog')) return;
        this.collapsePill();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') this.collapsePill();
      },
      { signal }
    );
  }

  /**
   * Selects a color swatch in the desktop pill without adding to cart.
   * @param {string} variantId
   * @param {Event} event
   */
  selectColor(variantId, event) {
    event.preventDefault();
    event.stopPropagation();

    const button =
      event.target instanceof HTMLButtonElement
        ? event.target
        : event.target instanceof Element
          ? event.target.closest('.quick-add__pill-color')
          : null;

    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[ref="variantId"]') ?? this.querySelector('input[name="id"]')
    );
    if (!variantInput || !variantId) return;

    variantInput.value = variantId;
    variantInput.disabled = false;

    for (const colorButton of this.querySelectorAll('.quick-add__pill-color')) {
      colorButton.setAttribute('aria-pressed', String(colorButton === button));
    }

    this.#updatePillSizeButtons();
  }

  /** @type {boolean} */
  #connectedSwitchInFlight = false;

  /**
   * Quick-buy modal: switch to a connected color product (gallery + sizes + ATC target).
   * @param {HTMLElement} input
   */
  async switchConnectedColorInModal(input) {
    if (!(input instanceof HTMLElement) || this.#connectedSwitchInFlight) return;

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const connectedUrl = input.dataset.connectedProductUrl;
    if (!connectedUrl) return;

    this.#connectedSwitchInFlight = true;

    try {
      const variantId = input.dataset.variantId || input.dataset.firstAvailableOrFirstVariantId;
      const targetUrl = new URL(connectedUrl, window.location.origin);
      if (variantId) targetUrl.searchParams.set('variant', variantId);

      if (productCard) {
        productCard.applyConnectedColorMedia(input);
        productCard.updateConnectedProductLinks(targetUrl.href);
        this.#syncCardColorSelection(productCard, input, connectedUrl, variantId);
      }

      const variantInput = /** @type {HTMLInputElement | null} */ (
        this.querySelector('[ref="variantId"]') ?? this.querySelector('input[name="id"]')
      );
      if (variantInput && variantId) {
        variantInput.value = variantId;
        variantInput.disabled = false;
      }

      this.#variantData = null;

      const dialog = document.getElementById('quick-add-dialog');
      if (dialog instanceof QuickAddDialog) {
        dialog.applyModalGalleryFromColorInput(input);
        dialog.setActiveQuickAdd(this);
      }

      const loadId = dialog instanceof QuickAddDialog ? dialog.beginContentLoad() : 0;
      const grid = await this.#ensureProductGrid(targetUrl.href);

      if (!grid) return;
      if (dialog instanceof QuickAddDialog && !dialog.isContentLoadCurrent(loadId)) return;

      // Skip sync that would re-select the previous product via change events
      this.updateQuickAddModal(/** @type {Element} */ (grid.cloneNode(true)), {
        preferredVariantId: variantId || null,
        dispatchVariantChange: false,
      });
    } finally {
      this.#connectedSwitchInFlight = false;
    }
  }

  /**
   * Keeps the product-card color radios in sync with the modal selection.
   * @param {Element} productCard
   * @param {HTMLElement} selectedInput
   * @param {string} connectedUrl
   * @param {string | undefined} variantId
   */
  #syncCardColorSelection(productCard, selectedInput, connectedUrl, variantId) {
    const optionValueId = selectedInput.dataset.optionValueId;
    for (const cardInput of productCard.querySelectorAll(
      '.product-card__color-swatch-label input[type="radio"], .product-card__inline-swatches input[type="radio"]'
    )) {
      if (!(cardInput instanceof HTMLInputElement)) continue;
      const matches =
        (variantId && cardInput.dataset.variantId === variantId) ||
        (optionValueId && cardInput.dataset.optionValueId === optionValueId) ||
        (connectedUrl && cardInput.dataset.connectedProductUrl === connectedUrl);
      cardInput.checked = Boolean(matches);
    }
  }

  /**
   * Selects the variant submitted by the inline size picker.
   * @param {string} variantId - The variant ID to add to cart.
   * @param {Event} event - The click event.
   */
  selectSize(variantId, event) {
    if (event instanceof Event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const button =
      event.target instanceof HTMLButtonElement
        ? event.target
        : event.target instanceof Element
          ? event.target.closest('.quick-add__size-option')
          : null;

    const form =
      button?.closest('form') ?? this.querySelector('product-form-component form');

    const variantInput = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('[ref="variantId"]') ?? form?.querySelector('input[name="id"]')
    );
    if (!variantInput || !variantId) return;

    variantInput.value = variantId;
    variantInput.disabled = false;

    const sizeButtons = this.querySelectorAll('.quick-add__size-option');
    for (const sizeButton of sizeButtons) {
      sizeButton.setAttribute(
        'aria-pressed',
        String(sizeButton === button || sizeButton.dataset.variantId === variantId)
      );
    }

    const modalSizePicker = document.querySelector('.quick-add-modal__size-picker');
    if (modalSizePicker) {
      for (const modalButton of modalSizePicker.querySelectorAll('.quick-add__size-option')) {
        modalButton.setAttribute('aria-pressed', String(modalButton.dataset.variantId === variantId));
      }
    }

    // Cart drawer: add immediately via lightweight fetch (never product-form submit).
    if (this.#isCartDrawerContext()) {
      this.#addVariantFromCartDrawer(variantId);
      return;
    }

    // Product cards: size click submits the form (native quick-buy behavior).
    if (form instanceof HTMLFormElement && button?.closest('.quick-add__pill-sizes, .quick-add__mobile')) {
      form.requestSubmit();
      return;
    }

    if (!isMobileBreakpoint() && button?.closest('.quick-add__pill-sizes')) {
      queueMicrotask(() => this.collapsePill());
    }
  }

  /**
   * @returns {boolean}
   */
  #isHotspotContext() {
    return this.closest('product-hotspot-component') !== null;
  }

  /**
   * @returns {boolean}
   */
  #isMegaMenuContext() {
    return this.closest('.menu-category-product-card--mega-menu') !== null;
  }

  /**
   * @returns {boolean}
   */
  #shouldShowMobileProductGallery() {
    return isMobileBreakpoint() || matchMedia('(hover: none)').matches;
  }

  /**
   * @returns {boolean}
   */
  #shouldShowHotspotProductImage() {
    if (!this.#isHotspotContext()) return false;
    return this.#shouldShowMobileProductGallery();
  }

  /**
   * Applies the product card gallery aspect ratio to the quick-add modal media.
   * Kept as a no-op: modal media now follows natural image height like the PDP.
   * @param {HTMLElement | null} _modalContent
   */
  #applyCardGalleryAspectRatio(_modalContent) {
    // Intentionally empty — height adapts to the image.
  }

  /**
   * Color options in quick-add keep their image swatches (centered via CSS).
   * @param {Element | null} _root
   */
  #restyleModalColorOptions(_root) {
    // No-op: swatches with images stay as-is; layout centers them in CSS.
  }

  /**
   * @param {HTMLElement | null} modalContent
   */
  #clearHotspotModalState(modalContent) {
    if (!modalContent) return;
    delete modalContent.dataset.hotspotProduct;
    delete modalContent.dataset.quickAddGallery;
    modalContent.querySelector('.quick-add-modal__hotspot-image')?.remove();
    modalContent.querySelector('.quick-add-modal__gallery')?.remove();
    if (modalContent instanceof HTMLElement) {
      modalContent.style.removeProperty('--card-gallery-aspect-ratio');
    }
  }

  /**
   * Strips zoom / sticky chrome so the PDP media gallery works inside the modal.
   * @param {Element} media
   */
  #prepareMediaForQuickAdd(media) {
    media.querySelectorAll('zoom-dialog, script, .product-media-container__zoom-button, .media-gallery__grid').forEach((el) => {
      el.remove();
    });
    media.querySelectorAll('.sticky-content, .sticky-content--desktop').forEach((el) => {
      el.classList.remove('sticky-content', 'sticky-content--desktop');
    });

    let eagerCount = 0;
    for (const img of media.querySelectorAll('img')) {
      if (!(img instanceof HTMLImageElement)) continue;
      if (eagerCount < 2) {
        img.loading = 'eager';
        img.fetchPriority = 'high';
        eagerCount += 1;
      } else if (!img.getAttribute('loading')) {
        img.loading = 'lazy';
      }
    }
  }

  /**
   * Collects product images from the card or hotspot for a lightweight carousel.
   * @returns {{ src: string, alt: string }[]}
   */
  #getGalleryImageSources() {
    /** @type {{ src: string, alt: string }[]} */
    const sources = [];
    const seen = new Set();

    const root =
      this.closest('product-card') ||
      this.closest('product-hotspot-component') ||
      this.closest('.menu-category-product-card');

    const imgs = root?.querySelectorAll(
      '.card-gallery slideshow-slide img, .card-gallery__primary-media img'
    );

    for (const img of imgs || []) {
      if (!(img instanceof HTMLImageElement)) continue;
      if (img.classList.contains('card-gallery__silhouette-image')) continue;
      const src = img.currentSrc || img.src;
      if (!src || seen.has(src)) continue;
      seen.add(src);
      sources.push({ src, alt: img.alt || '' });
      if (sources.length >= 6) break;
    }

    if (sources.length === 0) {
      const fallbackImgs = root?.querySelectorAll(
        '.hotspot-dialog__product-image, .menu-category-product-card__media img'
      );
      for (const img of fallbackImgs || []) {
        if (!(img instanceof HTMLImageElement)) continue;
        const src = img.currentSrc || img.src;
        if (!src || seen.has(src)) continue;
        seen.add(src);
        sources.push({ src, alt: img.alt || '' });
        if (sources.length >= 6) break;
      }
    }

    return sources;
  }

  /**
   * Builds a swipeable gallery carousel at the top of the mobile quick-add modal.
   * @param {HTMLElement | null} modalContent
   */
  #prependProductGalleryCarousel(modalContent) {
    if (!modalContent || !this.#shouldShowMobileProductGallery()) return;
    if (modalContent.querySelector('.product-information__media, .quick-add-modal__gallery')) return;

    const images = this.#getGalleryImageSources();
    if (images.length === 0) {
      this.#prependHotspotProductImage(modalContent);
      return;
    }

    this.#clearHotspotModalState(modalContent);

    const wrapper = document.createElement('div');
    wrapper.className = 'quick-add-modal__gallery';
    wrapper.setAttribute('role', 'region');
    wrapper.setAttribute('aria-roledescription', 'carousel');

    const track = document.createElement('div');
    track.className = 'quick-add-modal__gallery-track';

    images.forEach((image, index) => {
      const slide = document.createElement('div');
      slide.className = 'quick-add-modal__gallery-slide';
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${index + 1} / ${images.length}`);

      const img = document.createElement('img');
      img.src = image.src;
      img.alt = image.alt;
      img.loading = index === 0 ? 'eager' : 'lazy';
      img.decoding = 'async';
      slide.appendChild(img);
      track.appendChild(slide);
    });

    wrapper.appendChild(track);
    modalContent.prepend(wrapper);
    modalContent.dataset.quickAddGallery = 'true';
    this.#applyCardGalleryAspectRatio(modalContent);
    if (this.#isHotspotContext()) {
      modalContent.dataset.hotspotProduct = 'true';
    }
  }

  /**
   * Prepends the hotspot product image for mobile shop-the-look / hotspot taps.
   * @param {HTMLElement | null} modalContent
   */
  #prependHotspotProductImage(modalContent) {
    if (!modalContent || !this.#shouldShowHotspotProductImage()) return;
    if (modalContent.querySelector('.product-information__media, .quick-add-modal__gallery')) return;

    const hotspot = this.closest('product-hotspot-component');
    const sourceImage = hotspot?.querySelector(
      '.hotspot-dialog__product-image, .hotspot-dialog__placeholder-product-image'
    );
    if (!sourceImage) return;

    this.#clearHotspotModalState(modalContent);

    const wrapper = document.createElement('div');
    wrapper.className = 'quick-add-modal__hotspot-image';

    if (sourceImage instanceof HTMLImageElement) {
      const img = document.createElement('img');
      img.src = sourceImage.currentSrc || sourceImage.src;
      img.alt = sourceImage.alt;
      if (sourceImage.width) img.width = sourceImage.width;
      if (sourceImage.height) img.height = sourceImage.height;
      img.loading = 'eager';
      img.decoding = 'async';
      wrapper.appendChild(img);
    } else {
      wrapper.appendChild(sourceImage.cloneNode(true));
    }

    modalContent.prepend(wrapper);
    modalContent.dataset.hotspotProduct = 'true';
    this.#applyCardGalleryAspectRatio(modalContent);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  /** @type {((event: Event) => void) | null} */
  #boundModalSizeClick = null;

  handleClick = async (event) => {
    event.preventDefault();

    if (this.#modalOpenInFlight) return;

    // Never open a second showModal() dialog on top of the cart drawer — it freezes the page.
    if (this.#isCartDrawerContext()) {
      this.#handleCartDrawerQuickAdd();
      return;
    }

    const isMegaMenuDesktop = this.#isMegaMenuContext() && !isMobileBreakpoint();

    if (isMegaMenuDesktop) {
      if (
        this.dataset.quickAddButton === 'add' &&
        !this.querySelector('.quick-add__size-picker') &&
        !this.querySelector('.quick-add__color-picker')
      ) {
        return;
      }

      if (
        (this.querySelector('.quick-add__size-picker') || this.querySelector('.quick-add__color-picker')) &&
        this.dataset.productOptionsCount === '1'
      ) {
        return;
      }
    }

    // Instant path: card already has size/color buttons + images — never wait for PDP fetch.
    // (Previously mobile + gallery forced a full product HTML fetch and showed a long spinner.)
    if (
      (isMobileBreakpoint() || this.#isHotspotContext()) &&
      this.querySelector('.quick-add__size-picker, .quick-add__color-picker')
    ) {
      this.#openSizePickerModal();
      return;
    }

    if (
      (this.querySelector('.quick-add__size-picker') || this.querySelector('.quick-add__color-picker')) &&
      this.dataset.productOptionsCount === '1'
    ) {
      this.#openSizePickerModal();
      return;
    }

    this.#modalOpenInFlight = true;

    try {
      const openModalFirst = this.#isHotspotContext() || isMobileBreakpoint();
      const dialogComponent = document.getElementById('quick-add-dialog');
      const loadId =
        dialogComponent instanceof QuickAddDialog ? dialogComponent.beginContentLoad() : 0;
      const currentUrl = this.productPageUrl;
      let productGrid = this.#getCachedProductGrid(currentUrl);

      // Cache hit: render immediately — no loading flash.
      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);

        if (
          dialogComponent instanceof QuickAddDialog &&
          !dialogComponent.isContentLoadCurrent(loadId)
        ) {
          return;
        }

        this.#openQuickAddModal();
        return;
      }

      // Mobile/hotspot without card pickers: show title/price/gallery from the card now,
      // then upgrade with fetched variant picker when ready (no empty spinner wait).
      if (openModalFirst) {
        this.#renderInstantCardShell(dialogComponent);
        this.#openQuickAddModal();
      }

      productGrid = await this.#ensureProductGrid(currentUrl);

      if (
        dialogComponent instanceof QuickAddDialog &&
        !dialogComponent.isContentLoadCurrent(loadId)
      ) {
        return;
      }

      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      }

      if (!openModalFirst) {
        this.#openQuickAddModal();
      }
    } finally {
      this.#modalOpenInFlight = false;
    }
  };

  /**
   * Clears previous product HTML and shows a loading state before fetching.
   * @param {QuickAddDialog | Element | null} dialogComponent
   */
  #prepareModalForLoad(dialogComponent) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.resetSizeGuideState();
    }

    modalContent.replaceChildren();
    modalContent.dataset.view = 'product';
    delete modalContent.dataset.quickAddGallery;
    delete modalContent.dataset.hotspotProduct;

    const loading = document.createElement('div');
    loading.className = 'quick-add-modal__loading';
    loading.setAttribute('aria-busy', 'true');
    loading.setAttribute('aria-live', 'polite');

    const spinner = document.createElement('div');
    spinner.className = 'quick-add-modal__loading-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    loading.appendChild(spinner);
    modalContent.appendChild(loading);
  }

  /**
   * Instant mobile/hotspot shell from card DOM (title, price, gallery) while PDP HTML loads.
   * @param {QuickAddDialog | Element | null} dialogComponent
   */
  #renderInstantCardShell(dialogComponent) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.resetSizeGuideState();
      dialogComponent.setActiveQuickAdd(this);
    }

    const productCard = this.closest('product-card');
    const productLink =
      productCard?.querySelector('a[ref="productCardLink"]') || productCard?.querySelector('a[href*="/products/"]');

    const productHeader = document.createElement('div');
    productHeader.className = 'product-header';

    const titleLink = document.createElement('a');
    titleLink.textContent = this.dataset.productTitle || '';
    titleLink.href = productLink?.href || this.productPageUrl;
    productHeader.appendChild(titleLink);

    const cardPrice = productCard?.querySelector('product-price');
    if (cardPrice) {
      productHeader.appendChild(cardPrice.cloneNode(true));
    }

    const loading = document.createElement('div');
    loading.className = 'quick-add-modal__loading';
    loading.setAttribute('aria-busy', 'true');
    loading.setAttribute('aria-live', 'polite');
    const spinner = document.createElement('div');
    spinner.className = 'quick-add-modal__loading-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    loading.appendChild(spinner);

    modalContent.replaceChildren(productHeader, loading);
    modalContent.dataset.view = 'product';
    delete modalContent.dataset.quickAddGallery;
    delete modalContent.dataset.hotspotProduct;

    this.#prependProductGalleryCarousel(modalContent);
    this.#prependHotspotProductImage(modalContent);
    this.#injectSizeGuide(modalContent);
  }

  #isCartDrawerContext() {
    // Also treat similar-products modal as nested-dialog safe context
    // (same lightweight add path; avoids opening a second showModal dialog).
    return Boolean(this.closest('cart-drawer-component, similar-products-dialog'));
  }

  #getNestedDialogAddSource() {
    if (this.closest('similar-products-dialog')) return 'similar-products';
    return 'cart-drawer-quick-add';
  }

  /**
   * Add from cart-drawer recommendations without opening quick-add-dialog
   * or submitting product-form (that path freezes the open showModal drawer).
   */
  #handleCartDrawerQuickAdd() {
    const mobileSizePicker = this.querySelector('.quick-add__mobile .quick-add__size-picker');
    const mobileColorPicker = this.querySelector('.quick-add__mobile .quick-add__color-picker');
    const pillSizePicker = this.querySelector('.quick-add__pill .quick-add__size-picker');

    if (mobileSizePicker instanceof HTMLElement || mobileColorPicker instanceof HTMLElement) {
      this.dataset.cartDrawerSizesOpen = 'true';
      return;
    }

    if (pillSizePicker && !isMobileBreakpoint()) {
      this.#expandPill();
      return;
    }

    const form = this.querySelector('product-form-component form');
    const variantInput = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('[ref="variantId"]') ?? form?.querySelector('input[name="id"]')
    );
    const variantId = variantInput?.value;
    if (variantId) this.#addVariantFromCartDrawer(variantId);
  }

  /**
   * Lightweight cart/add for recommendations inside an open cart drawer.
   * @param {string} variantId
   */
  async #addVariantFromCartDrawer(variantId) {
    if (this.#drawerAddInFlight || !variantId) return;
    this.#drawerAddInFlight = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const productForm = this.querySelector('product-form-component');
      const form = productForm?.querySelector('form');
      const formData = form instanceof HTMLFormElement ? new FormData(form) : new FormData();
      formData.set('id', variantId);
      formData.set('quantity', String(formData.get('quantity') || '1'));

      // No sections — keep add snappy; open drawer already shows previous state then refreshes.

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });
      const json = await response.json();

      const addedQty = Number(formData.get('quantity') || 1);
      // Skip /cart.js round-trip — estimate from live bubble so the drawer can open sooner.
      let itemCount = estimateCartItemCountAfterAdd(addedQty);

      if (json.status) {
        try {
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          itemCount = cart.item_count ?? itemCount;
        } catch {
          // keep estimate
        }
      }

      const productId =
        (productForm instanceof HTMLElement && productForm.dataset.productId) || this.dataset.productId;

      const addSource = this.#getNestedDialogAddSource();

      if (json.status) {
        this.dispatchEvent(
          new CartAddEvent({}, this.id || variantId, {
            didError: true,
            source: addSource,
            itemCount,
            productId,
          })
        );
        return;
      }

      delete this.dataset.cartDrawerSizesOpen;
      this.#resetPillState();

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: addSource,
          itemCount,
          productId,
        })
      );
    } catch (error) {
      console.error('Cart drawer quick add failed:', error);
    } finally {
      this.#drawerAddInFlight = false;
      this.removeAttribute('aria-busy');
    }
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.setActiveQuickAdd(this);
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Prefetches and caches product grid HTML for snappy modal opens.
   * Safe to call from hover, focus, or intentional quick-add press — not from scroll visibility.
   * @param {string} [productPageUrl]
   * @param {{ force?: boolean }} [options]
   * @returns {Promise<Element | null>}
   */
  prefetchProductPage(productPageUrl = this.productPageUrl, options = {}) {
    if (!productPageUrl) return Promise.resolve(null);

    if (options.force) {
      const cacheKey = getQuickAddCacheKey(productPageUrl);
      quickAddGridCache.delete(cacheKey);
      quickAddGridInflight.delete(cacheKey);
    }

    return this.#ensureProductGrid(productPageUrl, { background: true });
  }

  /**
   * @param {string} productPageUrl
   * @returns {Element | null}
   */
  #getCachedProductGrid(productPageUrl) {
    if (!productPageUrl) return null;
    return quickAddGridCache.get(getQuickAddCacheKey(productPageUrl)) || null;
  }

  /**
   * Resolves cached grid or fetches once (deduped across instances).
   * @param {string} productPageUrl
   * @param {{ background?: boolean }} [options]
   * @returns {Promise<Element | null>}
   */
  #ensureProductGrid(productPageUrl, options = {}) {
    if (!productPageUrl) return Promise.resolve(null);

    const cacheKey = getQuickAddCacheKey(productPageUrl);
    const cached = quickAddGridCache.get(cacheKey);
    if (cached) return Promise.resolve(cached);

    const inflight = quickAddGridInflight.get(cacheKey);
    if (inflight) return inflight;

    const start = () => this.#fetchAndCacheProductGrid(productPageUrl, cacheKey);
    const request = options.background ? runQuickAddBackgroundFetch(start) : start();
    quickAddGridInflight.set(cacheKey, request);

    return request.finally(() => {
      if (quickAddGridInflight.get(cacheKey) === request) {
        quickAddGridInflight.delete(cacheKey);
      }
    });
  }

  /**
   * @param {string} productPageUrl
   * @param {string} cacheKey
   * @returns {Promise<Element | null>}
   */
  async #fetchAndCacheProductGrid(productPageUrl, cacheKey) {
    try {
      const grid = await this.#fetchProductGridElement(productPageUrl);
      if (grid) {
        setQuickAddCachedGrid(cacheKey, grid);
      }
      return grid;
    } catch (error) {
      console.error('Quick add product fetch failed:', error);
      return null;
    }
  }

  /**
   * Fetches product markup — prefers Section Rendering (`main`) for a smaller payload.
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #fetchProductGridElement(productPageUrl) {
    const sectionUrl = new URL(productPageUrl, window.location.origin);
    sectionUrl.searchParams.set('section_id', 'main');

    try {
      const sectionResponse = await fetch(sectionUrl.toString());
      if (sectionResponse.ok) {
        const sectionText = await sectionResponse.text();
        const sectionGrid = extractProductGridFromHtml(sectionText);
        if (sectionGrid) return sectionGrid;
      }
    } catch {
      // Fall through to full page fetch.
    }

    const pageUrl = new URL(productPageUrl, window.location.origin);
    pageUrl.searchParams.delete('section_id');
    const pageResponse = await fetch(pageUrl.toString());

    if (!pageResponse.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${pageResponse.status}`);
    }

    return extractProductGridFromHtml(await pageResponse.text());
  }

  /**
   * Fetches the product page content (and caches the product grid for later opens).
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const grid = await this.#ensureProductGrid(productPageUrl);
    if (!grid) return null;

    // Callers historically expect a Document; provide a minimal document with the grid.
    const doc = document.implementation.createHTMLDocument('');
    doc.body.appendChild(grid.cloneNode(true));
    return doc;
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @param {{ preferredVariantId?: string | null, dispatchVariantChange?: boolean }} [options]
   */
  updateQuickAddModal(productGrid, options = {}) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.resetSizeGuideState();
      dialogComponent.setActiveQuickAdd(this);
    }

    this.#clearHotspotModalState(modalContent);

    // Drop heavy / unused nodes before painting the modal.
    productGrid.querySelectorAll('script, noscript, sticky-add-to-cart, product-recommendations').forEach((el) => {
      el.remove();
    });

    const showGallery = this.#shouldShowMobileProductGallery();
    const media = productGrid.querySelector('.product-information__media');

    if (showGallery && media) {
      this.#prepareMediaForQuickAdd(media);
      productGrid.prepend(media);
    } else {
      media?.remove();
    }

    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const viewProductTitle = productGrid.querySelector('.view-product-title a');

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');

    if (viewProductTitle) {
      productHeader.appendChild(viewProductTitle);
    } else {
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;
      productHeader.appendChild(productTitle);
    }

    if (productPrice) {
      productHeader.appendChild(productPrice);
    }

    productDetails?.remove();

    // Keep media first when present; header follows.
    if (media?.parentElement === productGrid) {
      media.after(productHeader);
    } else {
      productGrid.prepend(productHeader);
    }
    if (variantPicker) {
      productGrid.appendChild(variantPicker);
    }
    if (productFormComponent) {
      productGrid.appendChild(productFormComponent);
    }

    // Full replace is much cheaper than morph for a fresh product open.
    modalContent.replaceChildren(...productGrid.childNodes);
    modalContent.dataset.view = 'product';

    if (showGallery && modalContent.querySelector('.product-information__media')) {
      modalContent.dataset.quickAddGallery = 'true';
      this.#applyCardGalleryAspectRatio(modalContent);
      if (this.#isHotspotContext()) {
        modalContent.dataset.hotspotProduct = 'true';
      }
    } else if (showGallery) {
      this.#prependProductGalleryCarousel(modalContent);
    } else {
      this.#prependHotspotProductImage(modalContent);
    }

    this.#injectSizeGuide(modalContent);
    this.#syncVariantSelection(modalContent, options);
    this.#restyleModalColorOptions(modalContent);
  }

  /**
   * Public entry used after morphs (e.g. color change inside the modal).
   * @param {Element} [modalContent]
   */
  reinjectSizeGuide(modalContent = document.getElementById('quick-add-modal-content')) {
    if (!(modalContent instanceof Element)) return;
    if (modalContent.dataset.view === 'size-guide') return;
    this.#injectSizeGuide(modalContent);
    this.#restyleModalColorOptions(modalContent);
  }

  /**
   * Injects the size guide trigger into the quick-add modal when configured.
   * Places it inside the Size / Talla legend so it sits beside the label.
   * @param {Element} modalContent
   */
  #injectSizeGuide(modalContent) {
    const dialog = document.getElementById('quick-add-dialog');
    if (!(dialog instanceof QuickAddDialog)) return;
    if (!shouldShowSizeGuideButton(this)) return;

    const label = dialog.dataset.sizeGuideLabel;
    if (!label) return;

    modalContent.querySelector('.quick-add-modal__size-guide')?.remove();
    modalContent.querySelector('.quick-add-modal__size-header-break')?.remove();
    modalContent.querySelector('.quick-add-modal__size-option-header')?.remove();
    modalContent.querySelector('.quick-add-modal__size-legend')?.classList.remove('quick-add-modal__size-legend');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'quick-add-modal__size-guide button button-unstyled';
    button.textContent = label;
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      dialog.openSizeGuidePanel(button, this);
    });

    const sizeFieldset = this.#findSizeOptionFieldset(modalContent);
    const sizeLegend = sizeFieldset?.querySelector(':scope > legend');

    if (sizeLegend instanceof HTMLElement) {
      sizeLegend.classList.add('quick-add-modal__size-legend');
      sizeLegend.appendChild(button);
      return;
    }

    const sizePicker = modalContent.querySelector('.quick-add-modal__size-picker');
    if (sizePicker instanceof HTMLElement) {
      const header = document.createElement('div');
      header.className = 'quick-add-modal__size-option-header';

      const title = document.createElement('span');
      title.className = 'h6 quick-add-modal__size-option-label';
      title.textContent = sizePicker.getAttribute('aria-label') || label;

      header.append(title, button);
      sizePicker.before(header);
      return;
    }

    // Fallback: show beside a synthetic «Size» label above the picker
    const variantPicker = modalContent.querySelector('variant-picker');
    const header = document.createElement('div');
    header.className = 'quick-add-modal__size-option-header';
    header.appendChild(button);

    if (variantPicker) {
      variantPicker.before(header);
      return;
    }

    modalContent.appendChild(header);
  }

  /**
   * Finds the non-swatch (size) option fieldset inside the modal.
   * @param {Element} modalContent
   * @returns {HTMLElement | null}
   */
  #findSizeOptionFieldset(modalContent) {
    const fieldsets = modalContent.querySelectorAll(
      'variant-picker .variant-option--buttons:not(.variant-option--swatches)'
    );

    if (!fieldsets.length) return null;

    const sizePosition = Number(this.dataset.sizeOptionPosition ?? NaN);

    if (sizePosition) {
      for (const fieldset of fieldsets) {
        if (!(fieldset instanceof HTMLElement)) continue;
        const optionId = fieldset.getAttribute('data-option-id') || '';
        const input = fieldset.querySelector('input[data-option-value-id], input[data-input-id]');
        const inputId = input?.getAttribute('data-input-id') || '';
        // data-input-id is "{position}-{index}"
        const positionFromInput = Number(inputId.split('-')[0] || NaN);

        if (positionFromInput === sizePosition) {
          return fieldset;
        }

        if (optionId.includes(String(sizePosition))) {
          return fieldset;
        }
      }
    }

    const first = fieldsets[0];
    return first instanceof HTMLElement ? first : null;
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {Event} event - The variant selected event
   */
  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;

    if (this.dataset.megaMenuDirectAdd === 'true') return;

    if (this.dataset.pillHasSizes !== 'true') {
      const productOptionsCount = Number(this.dataset.productOptionsCount ?? NaN);
      const quickAddButton = productOptionsCount <= 1 ? 'add' : 'choose';
      this.setAttribute('data-quick-add-button', quickAddButton);
    }

    const selectedVariantId = this.#getSelectedVariantId();
    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[ref="variantId"]') ?? this.querySelector('input[name="id"]')
    );

    if (selectedVariantId && variantInput) {
      variantInput.value = selectedVariantId;
      variantInput.disabled = false;
    }

    this.#updatePillSizeButtons();
  };

  /**
   * Opens the quick-add modal with the same size buttons as desktop.
   * Built entirely from card markup — no network wait.
   */
  #openSizePickerModal() {
    const modalContent = document.getElementById('quick-add-modal-content');
    const sizePicker = this.querySelector('.quick-add__size-picker');
    if (!modalContent || !sizePicker) return;

    if (this.#isMegaMenuContext() && !isMobileBreakpoint()) {
      return;
    }

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.beginContentLoad();
      dialogComponent.resetSizeGuideState();
      dialogComponent.setActiveQuickAdd(this);
    }

    const productCard = this.closest('product-card');
    const productLink =
      productCard?.querySelector('a[ref="productCardLink"]') || productCard?.querySelector('a[href*="/products/"]');

    const productHeader = document.createElement('div');
    productHeader.className = 'product-header';

    const titleLink = document.createElement('a');
    titleLink.textContent = this.dataset.productTitle || '';
    titleLink.href = productLink?.href || this.productPageUrl;
    productHeader.appendChild(titleLink);

    const cardPrice = productCard?.querySelector('product-price');
    if (cardPrice) {
      const priceClone = /** @type {Element} */ (cardPrice.cloneNode(true));
      priceClone
        .querySelectorAll(
          'swatches-variant-picker-component, .swatches, quick-add-component, .product-card-color-swatches, overflow-list'
        )
        .forEach((el) => el.remove());
      productHeader.appendChild(priceClone);
    }

    const modalSizePicker = document.createElement('div');
    modalSizePicker.className = 'quick-add-modal__size-picker';
    if (sizePicker.classList.contains('quick-add__color-picker')) {
      modalSizePicker.classList.add('quick-add__color-picker');
    }
    modalSizePicker.setAttribute('role', 'list');
    modalSizePicker.setAttribute('aria-label', sizePicker.getAttribute('aria-label') || 'Size');

    for (const sizeButton of sizePicker.querySelectorAll('.quick-add__size-option')) {
      const clone = /** @type {HTMLButtonElement} */ (sizeButton.cloneNode(true));
      clone.type = 'button';
      clone.removeAttribute('on:click');
      clone.classList.remove('quick-add__size-option--pill');
      modalSizePicker.appendChild(clone);
    }

    /** @type {Element[]} */
    const modalChildren = [productHeader];

    // Include product-card color options above sizes (same text format as sizes)
    const cardColorSwatches =
      productCard?.querySelector('.product-card__inline-swatches') ||
      productCard?.querySelector('.product-card__color-swatches') ||
      this.querySelector('.quick-add__color-picker');

    if (cardColorSwatches && !sizePicker.classList.contains('quick-add__color-picker')) {
      const colorWrap = document.createElement('div');
      colorWrap.className = 'quick-add-modal__color-swatches';
      colorWrap.appendChild(cardColorSwatches.cloneNode(true));
      modalChildren.push(colorWrap);
    }

    modalChildren.push(modalSizePicker);

    // Mount full content BEFORE opening — avoids height/scrollbar flash and misaligned bottom info.
    modalContent.replaceChildren(...modalChildren);
    modalContent.dataset.view = 'product';
    delete modalContent.dataset.quickAddGallery;
    delete modalContent.dataset.hotspotProduct;
    this.#prependProductGalleryCarousel(modalContent);
    this.#injectSizeGuide(modalContent);
    this.#restyleModalColorOptions(modalContent);

    if (this.#boundModalSizeClick) {
      modalContent.removeEventListener('click', this.#boundModalSizeClick);
    }

    this.#boundModalSizeClick = (clickEvent) => {
      const button = clickEvent.target.closest('.quick-add-modal__size-picker .quick-add__size-option--available');
      if (!(button instanceof HTMLButtonElement) || !clickEvent.isTrusted) return;

      clickEvent.preventDefault();

      const variantId = button.dataset.variantId;
      if (!variantId) return;

      this.selectSize(variantId, clickEvent);

      const productForm = /** @type {Element | null} */ (this.querySelector('product-form-component'));
      const dialogForAdd = document.getElementById('quick-add-dialog');

      if (dialogForAdd instanceof QuickAddDialog && productForm) {
        dialogForAdd.addVariantToCart(variantId, productForm);
        return;
      }

      /** @type {{ handleSubmit?: (event: Event) => void } | null} */ (productForm)?.handleSubmit?.(
        new Event('submit', { bubbles: true, cancelable: true })
      );
    };

    modalContent.addEventListener('click', this.#boundModalSizeClick);

    if (dialogComponent) {
      dialogComponent.addEventListener(
        DialogCloseEvent.eventName,
        () => {
          if (this.#boundModalSizeClick) {
            modalContent.removeEventListener('click', this.#boundModalSizeClick);
            this.#boundModalSizeClick = null;
          }
        },
        { once: true }
      );
    }

    this.#openQuickAddModal();

    // Warm cache in background for later full-picker opens — never blocks this open.
    this.prefetchProductPage();
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   * @param {{ preferredVariantId?: string | null, dispatchVariantChange?: boolean }} [options]
   */
  #syncVariantSelection(modalContent, options = {}) {
    const { preferredVariantId = null, dispatchVariantChange = true } = options;
    const selectedVariantId = preferredVariantId || this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (!(input instanceof HTMLInputElement) || input.dataset.variantId !== selectedVariantId) continue;

      input.checked = true;

      // Never re-fire connected-color changes — that snaps back to the previous product
      if (!dispatchVariantChange || input.dataset.connectedProductUrl) break;

      input.dispatchEvent(new Event('change', { bubbles: true }));
      break;
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #modalAddInFlight = false;
  #awaitingModalVariantAdd = false;
  /** @type {number} */
  #contentLoadId = 0;
  /** @type {QuickAddComponent | null} */
  #activeQuickAdd = null;
  /** @type {HTMLElement | null} */
  #sizeGuideTrigger = null;
  /** @type {DocumentFragment | null} */
  #productPanelSnapshot = null;
  /** @type {((event: KeyboardEvent) => void) | null} */
  #sizeGuideEscapeHandler = null;
  /** @type {((event: Event) => void) | null} */
  #closeButtonSizeGuideHandler = null;

  /**
   * Starts a new modal content load; newer loads invalidate older ones.
   * @returns {number}
   */
  beginContentLoad() {
    this.#contentLoadId += 1;
    return this.#contentLoadId;
  }

  /**
   * @param {number} loadId
   * @returns {boolean}
   */
  isContentLoadCurrent(loadId) {
    return loadId === this.#contentLoadId;
  }

  /**
   * @param {QuickAddComponent | null} component
   */
  setActiveQuickAdd(component) {
    this.#activeQuickAdd = component;
  }

  /**
   * @returns {QuickAddComponent | null}
   */
  getActiveQuickAdd() {
    return this.#activeQuickAdd;
  }

  /**
   * Rebuilds the modal gallery carousel from a color swatch's gallery / pattern data.
   * @param {HTMLElement} input
   */
  applyModalGalleryFromColorInput(input) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement) || !(input instanceof HTMLElement)) return;

    const gallerySrcs = (input.dataset.gallerySrcs || '')
      .split('|')
      .map((src) => src.trim())
      .filter(Boolean);
    const patternSrc = input.dataset.colorPatternSrc || '';
    const urls = gallerySrcs.length ? gallerySrcs : patternSrc ? [patternSrc] : [];

    if (urls.length) {
      modalContent.querySelector('.quick-add-modal__gallery')?.remove();
      modalContent.querySelector('.quick-add-modal__hotspot-image')?.remove();
      modalContent.querySelector('.product-information__media')?.remove();

      const wrapper = document.createElement('div');
      wrapper.className = 'quick-add-modal__gallery';
      wrapper.setAttribute('role', 'region');

      const track = document.createElement('div');
      track.className = 'quick-add-modal__gallery-track';

      urls.forEach((src, index) => {
        const slide = document.createElement('div');
        slide.className = 'quick-add-modal__gallery-slide';
        const img = document.createElement('img');
        img.src = src;
        img.alt = input.getAttribute('aria-label') || '';
        img.loading = index === 0 ? 'eager' : 'lazy';
        img.decoding = 'async';
        slide.appendChild(img);
        track.appendChild(slide);
      });

      wrapper.appendChild(track);
      modalContent.prepend(wrapper);
      modalContent.dataset.quickAddGallery = 'true';
      return;
    }

    const mediaId = input.dataset.optionMediaId;
    const slideshow = /** @type {{ select?: Function } | null} */ (
      modalContent.querySelector('slideshow-component')
    );
    if (mediaId && slideshow && typeof slideshow.select === 'function') {
      slideshow.select({ id: String(mediaId) }, undefined, { animate: false });
    }
  }

  /**
   * Clears size-guide UI/listeners without restoring product DOM (used before loading a new product).
   */
  resetSizeGuideState() {
    const closeButton = this.refs.closeButton;
    if (closeButton && this.#closeButtonSizeGuideHandler) {
      closeButton.removeEventListener('click', this.#closeButtonSizeGuideHandler, true);
      this.#closeButtonSizeGuideHandler = null;
    }

    if (this.#sizeGuideEscapeHandler) {
      document.removeEventListener('keydown', this.#sizeGuideEscapeHandler, true);
      this.#sizeGuideEscapeHandler = null;
    }

    this.#productPanelSnapshot = null;
    this.#sizeGuideTrigger = null;

    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent) {
      modalContent.querySelector('[data-quick-add-size-guide-view]')?.remove();
      for (const child of modalContent.querySelectorAll('[data-quick-add-panel-hidden]')) {
        if (!(child instanceof HTMLElement)) continue;
        child.hidden = false;
        child.removeAttribute('data-quick-add-panel-hidden');
      }
      modalContent.dataset.view = 'product';
      modalContent.classList.remove(
        'quick-add-modal__content--exit-to-right',
        'quick-add-modal__content--enter-from-left'
      );
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#onModalVariantUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#syncModalGalleryFromVariant, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#reinjectSizeGuideAfterMorph, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener('change', this.#onModalOptionChange, true);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    const parentCloseDialog = this.closeDialog.bind(this);
    this.closeDialog = async () => {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent?.dataset.view === 'size-guide') {
        await this.closeSizeGuidePanel();
        return;
      }
      await parentCloseDialog();
    };
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener('change', this.#onModalOptionChange, true);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Marks that the next variant update inside the modal should add to cart.
   * Color swatches only update image/sizes — they do not add to cart.
   * @param {Event} event
   */
  #onModalOptionChange = (event) => {
    if (!this.refs.dialog?.open || !event.isTrusted || this.#modalAddInFlight) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent?.contains(event.target)) return;

    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;

    const isColorSwatch =
      target instanceof Element &&
      Boolean(
        target.closest('.variant-option--swatches') ||
          target.closest('.variant-option__button-label--has-swatch') ||
          target.closest('.product-card__color-swatch-label') ||
          target.closest('.quick-add-modal__color-swatches')
      );

    if (isColorSwatch && target instanceof HTMLInputElement) {
      this.#awaitingModalVariantAdd = false;

      // Cloned card swatches: VariantPicker may not run if listeners didn't attach — handle here too
      const connectedUrl = target.dataset.connectedProductUrl;
      const quickAdd = this.#activeQuickAdd;
      if (connectedUrl && quickAdd instanceof QuickAddComponent) {
        quickAdd.switchConnectedColorInModal(target);
        return;
      }

      this.applyModalGalleryFromColorInput(target);
      const variantId = target.dataset.variantId || target.dataset.firstAvailableOrFirstVariantId;
      if (variantId && quickAdd instanceof QuickAddComponent) {
        const fakeEvent = new Event('click', { bubbles: true, cancelable: true });
        quickAdd.selectColor(variantId, fakeEvent);
      }
      return;
    }

    if (target instanceof HTMLInputElement) {
      if (target.type !== 'radio' || target.dataset.optionAvailable === 'false') return;

      const variantId = target.dataset.variantId;
      if (variantId) {
        const productForm = modalContent?.querySelector('product-form-component');
        if (productForm) {
          this.#addModalVariantToCartById(variantId, productForm);
        }
        return;
      }
    } else if (!target.classList.contains('variant-option__select')) {
      return;
    }

    this.#awaitingModalVariantAdd = true;
  };

  /**
   * Keeps the modal gallery in sync when a color/variant changes.
   * @param {CustomEvent} event
   */
  #syncModalGalleryFromVariant = (event) => {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const eventTarget = event.target;
    if (eventTarget instanceof Element && !modalContent.contains(eventTarget) && eventTarget !== this) {
      return;
    }

    requestAnimationFrame(() => {
      const media = modalContent.querySelector('.product-information__media');
      if (media) {
        media.querySelectorAll('zoom-dialog').forEach((el) => el.remove());
        media.querySelectorAll('.product-media-container__zoom-button').forEach((el) => el.remove());
        media.querySelectorAll('.media-gallery__grid').forEach((el) => el.remove());
        media.querySelectorAll('.sticky-content, .sticky-content--desktop').forEach((el) => {
          el.classList.remove('sticky-content', 'sticky-content--desktop');
        });
      }

      const mediaId = event.detail?.resource?.featured_media?.id;
      const slideshow = /** @type {{ select?: Function } | null} */ (
        modalContent.querySelector('slideshow-component')
      );
      if (mediaId && slideshow && typeof slideshow.select === 'function') {
        slideshow.select({ id: String(mediaId) }, undefined, { animate: false });
      }

      const previewSrc = event.detail?.resource?.featured_media?.preview_image?.src;
      if (previewSrc) {
        const fallbackImg = modalContent.querySelector(
          '.quick-add-modal__gallery-slide img, .quick-add-modal__hotspot-image img'
        );
        if (fallbackImg instanceof HTMLImageElement) {
          fallbackImg.src = previewSrc;
        }
      }
    });
  };

  /**
   * Size guide lives inside the size <legend>; morph on variant change removes it.
   * @param {CustomEvent} event
   */
  #reinjectSizeGuideAfterMorph = (event) => {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent || modalContent.dataset.view === 'size-guide') return;

    const eventTarget = event.target;
    if (eventTarget instanceof Element && !modalContent.contains(eventTarget) && eventTarget !== this) {
      return;
    }

    const quickAdd =
      this.#activeQuickAdd ||
      document.querySelector('quick-add-component[stay-visible]') ||
      document.querySelector('quick-add-component');

    if (quickAdd instanceof QuickAddComponent) {
      requestAnimationFrame(() => quickAdd.reinjectSizeGuide(modalContent));
    }
  };

  /**
   * Adds to cart when a variant is updated after a user selection in the modal.
   * @param {CustomEvent} event
   */
  #onModalVariantUpdate = (event) => {
    if (!this.#awaitingModalVariantAdd || this.#modalAddInFlight) return;
    if (!(event.target instanceof Element) || !event.target.closest('#quick-add-modal-content')) return;

    this.#awaitingModalVariantAdd = false;

    const resource = event.detail?.resource;
    if (!resource?.available || !resource.id) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    const productForm = modalContent?.querySelector('product-form-component');
    if (productForm) {
      this.#addModalVariantToCartById(String(resource.id), productForm);
    }
  };

  /**
   * Adds a variant to cart from the quick-add modal (size picker or variant picker).
   * @param {string} variantId
   * @param {Element} productFormElement
   */
  addVariantToCart(variantId, productFormElement) {
    return this.#addModalVariantToCartById(variantId, productFormElement);
  }

  /**
   * @param {string} variantId
   * @param {Element} productFormElement
   */
  async #addModalVariantToCartById(variantId, productFormElement) {
    if (this.#modalAddInFlight) return;

    const form = productFormElement.querySelector('form');
    if (!form) return;

    const variantInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    if (variantInput) {
      variantInput.value = variantId;
      variantInput.disabled = false;
    }

    this.#modalAddInFlight = true;

    const cartDrawer = /** @type {(HTMLElement & { openNow?: () => void }) | null} */ (
      document.querySelector('cart-drawer-component[auto-open]')
    );

    // Instant handoff: close quick-add + open cart before waiting on the network.
    // Content refreshes as soon as the lightweight cart/add succeeds.
    if (cartDrawer && this.refs.dialog?.open) {
      this.closeDialogImmediate({ keepBodyLock: true });
    }
    if (cartDrawer && typeof cartDrawer.openNow === 'function') {
      cartDrawer.dataset.cartPending = 'true';
      cartDrawer.openNow();
    }

    try {
      const formData = new FormData(form);
      formData.set('id', variantId);

      const quantity = formData.get('quantity') || productFormElement.dataset.quantityDefault || '1';
      formData.set('quantity', String(quantity));

      // No `sections` — Shopify section rendering is the main delay on cart/add.
      // Drawer HTML refreshes via Section Rendering API after CartAddEvent.

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });
      const json = await response.json();

      let totalCartCount = estimateCartItemCountAfterAdd(quantity);

      if (json.status) {
        try {
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          totalCartCount = cart.item_count ?? totalCartCount;
        } catch {
          // keep estimate
        }

        productFormElement.dispatchEvent(
          new CartAddEvent({}, productFormElement.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: totalCartCount,
            productId: productFormElement.dataset.productId,
          })
        );
        return;
      }

      productFormElement.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'product-form-component',
          itemCount: totalCartCount,
          productId: productFormElement.dataset.productId,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      cartDrawer?.removeAttribute('data-cart-pending');
      this.#modalAddInFlight = false;
    }
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (!this.refs.dialog?.open) return;
    if (event.detail.data.didError) return;

    // Cart drawer auto-open: skip ~0.5s close animation and keep body lock for handoff.
    if (document.querySelector('cart-drawer-component[auto-open]')) {
      this.closeDialogImmediate({ keepBodyLock: true });
      return;
    }

    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    this.#awaitingModalVariantAdd = false;
    this.#modalAddInFlight = false;
    this.#activeQuickAdd = null;
    this.resetSizeGuideState();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  /**
   * Shows the size guide inside the quick buy modal with a slide-from-right transition.
   * @param {HTMLElement} trigger
   * @param {QuickAddComponent} quickAddComponent
   */
  openSizeGuidePanel(trigger, quickAddComponent) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent || modalContent.dataset.view === 'size-guide') return;
    if (modalContent.querySelector('.quick-add-modal__loading')) return;

    const quickAdd =
      quickAddComponent instanceof QuickAddComponent
        ? quickAddComponent
        : this.#activeQuickAdd || trigger.closest('quick-add-component');
    let contentNodes = getSizeGuideContentNodes(quickAdd);

    if (!contentNodes || contentNodes.length === 0) {
      if (!isSizeGuideConfigured()) return;
      contentNodes = [createSizeGuideEmptyMessage(this)];
    }

    this.#sizeGuideTrigger = trigger;
    // Keep product panel in the DOM (hidden) so mulaya-size-guide can sync
    // size buttons / ATC with the quick-add variant picker + form.
    this.#productPanelSnapshot = null;
    for (const child of [...modalContent.children]) {
      if (!(child instanceof HTMLElement)) continue;
      child.hidden = true;
      child.setAttribute('data-quick-add-panel-hidden', '');
    }

    const view = document.createElement('div');
    view.className = 'quick-add-modal__size-guide-view quick-add-modal__size-guide-view--enter-from-right';
    view.dataset.quickAddSizeGuideView = 'true';

    const backButton = document.createElement('button');
    backButton.type = 'button';
    backButton.className = 'quick-add-modal__back button button-unstyled';
    backButton.textContent = this.dataset.sizeGuideBackLabel || 'Back';
    backButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.closeSizeGuidePanel();
    });

    const title = document.createElement('h2');
    title.className = 'quick-add-modal__size-guide-title';
    title.id = 'quick-add-active-size-guide-title';
    title.tabIndex = -1;
    title.textContent = this.dataset.sizeGuideLabel || '';

    const body = document.createElement('div');
    body.className = 'quick-add-modal__size-guide-body';
    body.append(...contentNodes);

    view.append(backButton, title, body);
    modalContent.appendChild(view);
    modalContent.dataset.view = 'size-guide';

    view.addEventListener(
      'animationend',
      () => {
        view.classList.remove('quick-add-modal__size-guide-view--enter-from-right');
      },
      { once: true }
    );

    const closeButton = this.refs.closeButton;
    if (closeButton) {
      this.#closeButtonSizeGuideHandler = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.closeSizeGuidePanel();
      };
      closeButton.addEventListener('click', this.#closeButtonSizeGuideHandler, true);
    }

    this.#sizeGuideEscapeHandler = (event) => {
      if (event.key !== 'Escape' || modalContent.dataset.view !== 'size-guide') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.closeSizeGuidePanel();
    };
    document.addEventListener('keydown', this.#sizeGuideEscapeHandler, true);

    requestAnimationFrame(() => {
      title.focus();
    });
  }

  /**
   * Returns to the product panel inside the quick buy modal.
   * @param {{ restoreFocus?: boolean, animate?: boolean }} [options]
   */
  closeSizeGuidePanel = async (options = {}) => {
    const { restoreFocus = true, animate = true } = options;
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent || modalContent.dataset.view !== 'size-guide') return;

    const closeButton = this.refs.closeButton;
    if (closeButton && this.#closeButtonSizeGuideHandler) {
      closeButton.removeEventListener('click', this.#closeButtonSizeGuideHandler, true);
      this.#closeButtonSizeGuideHandler = null;
    }

    if (this.#sizeGuideEscapeHandler) {
      document.removeEventListener('keydown', this.#sizeGuideEscapeHandler, true);
      this.#sizeGuideEscapeHandler = null;
    }

    const restoreProductPanel = () => {
      modalContent.querySelector('[data-quick-add-size-guide-view]')?.remove();
      for (const child of modalContent.querySelectorAll('[data-quick-add-panel-hidden]')) {
        if (!(child instanceof HTMLElement)) continue;
        child.hidden = false;
        child.removeAttribute('data-quick-add-panel-hidden');
      }
      modalContent.dataset.view = 'product';
      this.#productPanelSnapshot = null;
      modalContent.classList.remove('quick-add-modal__content--exit-to-right', 'quick-add-modal__content--enter-from-left');

      const trigger = this.#sizeGuideTrigger;
      this.#sizeGuideTrigger = null;

      if (restoreFocus && trigger && document.contains(trigger)) {
        requestAnimationFrame(() => trigger.focus());
      }
    };

    if (!animate) {
      restoreProductPanel();
      return;
    }

    const activeView = modalContent.querySelector('.quick-add-modal__size-guide-view');
    if (activeView) {
      activeView.classList.add('quick-add-modal__size-guide-view--exit-to-right');
    }

    const animationDuration = 500;
    await new Promise((resolve) => {
      /** @type {number} */
      let timeoutId;
      const handleAnimationEnd = (event) => {
        if (event.target !== activeView) return;
        clearTimeout(timeoutId);
        activeView?.removeEventListener('animationend', handleAnimationEnd);
        resolve(undefined);
      };

      if (activeView) {
        activeView.addEventListener('animationend', handleAnimationEnd);
      }
      timeoutId = window.setTimeout(() => resolve(undefined), animationDuration + 50);
    });

    restoreProductPanel();
    modalContent.classList.add('quick-add-modal__content--enter-from-left');

    requestAnimationFrame(() => {
      modalContent.classList.remove('quick-add-modal__content--enter-from-left');
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
